import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import TrustBadge from "../components/TrustBadge";
import {
  Search, Scale, Compass, ArrowRight, MapPin, Landmark, Timer, ClipboardList, FileSignature,
} from "lucide-react";

export default function CaseFilingTemplatesPage() {
  const [items, setItems] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (jurisdiction) params.jurisdiction = jurisdiction;
      if (category) params.category = category;
      const { data } = await api.get("/case-filing-templates", { params });
      setItems(data.items || []);
      setJurisdictions(data.jurisdictions || []);
      setCategories(data.categories || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jurisdiction, category]);

  return (
    <div data-testid="case-filing-templates-page">
      <PageHeader
        eyebrow="La Bibliothèque · Case filing"
        title="Something happened. What can you legally do about it?"
        description="Structured, jurisdiction-aware templates for common causes of action — with the elements you'd need to prove, the evidence usually required, the limitation window, and the appropriate forum."
        action={
          <Link to="/app/cases/claim-explorer">
            <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="open-claim-explorer-btn">
              <Compass size={14} className="mr-1.5" /> Open Claim Explorer <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" />
            <Input
              placeholder="Describe what happened…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-none pl-9 border-[#E8E3F0]"
              data-testid="case-filing-search-input"
            />
          </div>
          <Button type="submit" variant="outline" className="rounded-none border-[#E8E3F0]" data-testid="case-filing-search-btn">Search</Button>
        </form>
        <select value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} className="border border-[#E8E3F0] bg-white px-3 py-2 text-sm" data-testid="case-filing-jurisdiction-filter">
          <option value="">All jurisdictions</option>
          {jurisdictions.map((j) => <option key={j} value={j}>{j}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="border border-[#E8E3F0] bg-white px-3 py-2 text-sm" data-testid="case-filing-category-filter">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: template list */}
        <div className="lg:col-span-1 space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="label-eyebrow">Loading…</div>
          ) : items.length === 0 ? (
            <EmptyState icon={Scale} title="No matching templates" description="Try broadening the filters or open the Claim Explorer." />
          ) : (
            items.map((t) => {
              const active = selected?.id === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelected(t)}
                  data-testid={`case-filing-item-${t.slug}`}
                  className={`w-full text-left border p-4 transition-colors ${
                    active
                      ? "border-[#2A0F5A] bg-[#2A0F5A] text-[#FAFAF8]"
                      : "border-[#E8E3F0] bg-white hover:border-[#7B56C9]"
                  }`}
                >
                  <div className={`label-eyebrow mb-1 ${active ? "!text-[#C5A059]" : "text-[#C5A059]"}`}>{t.jurisdiction} · {t.category}</div>
                  <div className={`font-serif text-lg leading-tight ${active ? "" : "text-[#2A0F5A]"}`}>{t.title}</div>
                  <div className={`text-xs mt-1 ${active ? "text-[#FAFAF8]/75" : "text-[#4A5568]"}`}>{t.summary}</div>
                </button>
              );
            })
          )}
        </div>

        {/* Right: detail panel */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="border border-dashed border-[#E8E3F0] p-10 text-center bg-white" data-testid="case-filing-empty-detail">
              <Compass size={28} strokeWidth={1.3} className="mx-auto text-[#C5A059] mb-4" />
              <div className="font-serif text-2xl mb-2">Pick a template on the left</div>
              <div className="text-sm text-[#4A5568] max-w-md mx-auto">Or open the Claim Explorer to describe your situation in your own words and let Marianne map it.</div>
            </div>
          ) : (
            <div className="border border-[#E8E3F0] bg-white p-6 md:p-8 space-y-6" data-testid="case-filing-detail">
              <div>
                <div className="label-eyebrow text-[#C5A059] mb-2">{selected.jurisdiction} · {selected.category}</div>
                <h2 className="font-serif text-3xl md:text-4xl leading-tight">{selected.title}</h2>
                <p className="text-[#2A0F5A]/75 mt-3">{selected.summary}</p>
              </div>

              <TrustBadge trust={selected.trust} />

              <Section title="Possible claim" icon={Scale}>
                <p className="text-sm">{selected.possible_claim}</p>
              </Section>

              <Section title="Elements usually required" icon={ClipboardList}>
                <BulletList items={selected.elements_required} />
              </Section>

              <Section title="Evidence usually needed" icon={FileSignature}>
                <BulletList items={selected.evidence_needed} />
              </Section>

              <Section title="Limitation" icon={Timer}>
                <p className="text-sm">{selected.limitation_note || <em className="text-[#4A5568]">Varies by jurisdiction.</em>}</p>
              </Section>

              <Section title="Pre-filing steps" icon={MapPin}>
                <BulletList items={selected.pre_filing_steps} />
              </Section>

              <Section title="Suggested forums" icon={Landmark}>
                <BulletList items={selected.suggested_forums} />
              </Section>

              <div className="flex flex-wrap gap-2 pt-2">
                <Link to="/app/cases/new">
                  <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="prepare-case-btn">
                    <Scale size={14} className="mr-1.5" /> Prepare a case in Marianne <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </Link>
                <Link to="/app/cases/claim-explorer" state={{ prefill: selected.summary }}>
                  <Button variant="outline" className="rounded-none border-[#E8E3F0]" data-testid="explore-from-template-btn">
                    <Compass size={14} className="mr-1.5" /> Explore my situation
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-[#7B56C9]" />
        <div className="label-eyebrow">{title}</div>
      </div>
      {children}
    </div>
  );
}

function BulletList({ items }) {
  if (!items || items.length === 0) return <div className="text-sm text-[#4A5568] italic">Not specified — jurisdiction-dependent.</div>;
  return (
    <ul className="text-sm space-y-1.5 list-disc pl-5">
      {items.map((i, k) => <li key={k}>{i}</li>)}
    </ul>
  );
}
